import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  CreateDsmRoomReplacementRulesPayload,
  DsmRoomReplacementRuleCopyResponse,
  DsmRoomReplacementRuleCopyResult,
  DsmRoomReplacementRuleCopyTarget,
  DsmRoomReplacementRuleDetail,
  DsmRoomReplacementRuleExecutedLot,
  DsmRoomReplacementRulePendingLot,
  DsmRoomReplacementRulesQuery,
  DsmRoomReplacementRuleSummary,
  DsmRoomReplacementRuleValidation,
  UpdateDsmRoomReplacementRulePayload
} from '../models/dsm-room-replacement-rule.models';

@Injectable({ providedIn: 'root' })
export class DsmRoomReplacementRulesService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #baseUrl = inject(API_CONFIG).baseUrlV2;

  /**
   * Global list across projects. Unpaged and already sorted newest first, so the
   * table only has to apply its own text search on top.
   */
  readAllRules(query: DsmRoomReplacementRulesQuery = {}): Observable<DsmRoomReplacementRuleSummary[]> {
    let params = new HttpParams();

    const filters: Record<string, string | null | undefined> = {
      projectId: query.projectId,
      modelId: query.modelId,
      elevationId: query.elevationId
      // triggerProductId: query.triggerProductId, // Floor Plan filter removed from the list.
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params = params.set(key, value);
    });

    if (query.includeInactive) {
      params = params.set('includeInactive', true);
    }

    return this.#api
      .getData<DsmRoomReplacementRuleSummary[]>(`${this.#baseUrl}/room-replacement-rules`, {
        showLoader: false,
        showMessage: false,
        options: { params }
      })
      .pipe(map((rules: DsmRoomReplacementRuleSummary[] | null) => rules ?? []));
  }

  /**
   * One rule by id alone — global, so it needs no project or model. Note the response
   * does *not* name the project: it carries `modelId` and `elevationId` only.
   */
  readRule(ruleId: string): Observable<DsmRoomReplacementRuleDetail | null> {
    return this.#api
      .getData<DsmRoomReplacementRuleDetail>(this.#ruleUrl(ruleId), {
        showLoader: false,
        showMessage: false,
        showError: false
      })
      .pipe(map((rule: DsmRoomReplacementRuleDetail | null) => rule ?? null));
  }

  /**
   * Duplicate pre-check: is this floor plan free on this model + elevation? Scoped by
   * model rather than project, so it needs no project id.
   *
   * `sourceRuleId` turns it into the copy pre-check: the target must also hold every
   * room that rule references, which is what the copy itself would otherwise refuse on.
   */
  validateRule(
    modelId: string,
    elevationId: string,
    triggerProductId: string,
    // The copy dialog checks every elevation of a model at once, where one toast per
    // failed check would bury the screen; it reports them on the chips instead.
    showError = true,
    sourceRuleId?: string | null
  ): Observable<DsmRoomReplacementRuleValidation> {
    let params = new HttpParams()
      .set('modelId', modelId)
      .set('elevationId', elevationId)
      .set('triggerProductId', triggerProductId);

    if (sourceRuleId) params = params.set('sourceRuleId', sourceRuleId);

    return this.#api.getData<DsmRoomReplacementRuleValidation>(`${this.#baseUrl}/room-replacement-rules/validate`, {
      showLoader: false,
      showMessage: false,
      showError,
      options: { params }
    });
  }

  /**
   * Global create — keyed on the model in the body, so it needs no project id. One rule
   * comes back per elevation in `elevationIds`, in that order.
   *
   * Errors surface inside the form, so neither a loader nor a toast is raised here.
   */
  createRules(payload: CreateDsmRoomReplacementRulesPayload): Observable<DsmRoomReplacementRuleDetail[]> {
    return this.#api
      .postData<
        DsmRoomReplacementRuleDetail[]
      >(`${this.#baseUrl}/room-replacement-rules`, { showLoader: false, showMessage: false, showError: false }, payload)
      .pipe(map((rules: DsmRoomReplacementRuleDetail[] | null) => rules ?? []));
  }

  /**
   * Activate or deactivate a rule. Activation can be rejected when another active
   * rule holds the same floor plan — the API error toast carries that message.
   */
  toggleRuleActive(ruleId: string, isActive: boolean): Observable<DsmRoomReplacementRuleDetail> {
    return this.#api.patchData<DsmRoomReplacementRuleDetail>(
      `${this.#ruleUrl(ruleId)}/toggle-active`,
      { isActive },
      { showLoader: false, showMessage: false }
    );
  }

  /** Send the whole rule — the backend replaces both room lists wholesale. */
  updateRule(ruleId: string, payload: UpdateDsmRoomReplacementRulePayload): Observable<DsmRoomReplacementRuleDetail> {
    return this.#api.putData<DsmRoomReplacementRuleDetail>(this.#ruleUrl(ruleId), payload, {
      showLoader: false,
      showMessage: false,
      showError: false
    });
  }

  /**
   * Copy this rule onto other model + elevation targets in one call. Nothing is
   * all-or-nothing: valid targets are created and the rest come back as skips with a
   * reason, so the caller must read every result rather than the request's success.
   *
   * Failures are shown inside the dialog, hence no toast.
   */
  copyRule(
    ruleId: string,
    targets: DsmRoomReplacementRuleCopyTarget[]
  ): Observable<DsmRoomReplacementRuleCopyResult[]> {
    return this.#api
      .postData<DsmRoomReplacementRuleCopyResponse>(
        `${this.#ruleUrl(ruleId)}/copy`,
        { showLoader: false, showMessage: false, showError: false },
        { targets }
      )
      .pipe(map((response: DsmRoomReplacementRuleCopyResponse | null) => response?.results ?? []));
  }

  readPendingLots(ruleId: string): Observable<DsmRoomReplacementRulePendingLot[]> {
    return this.#api
      .getData<DsmRoomReplacementRulePendingLot[]>(`${this.#ruleUrl(ruleId)}/pending-lots`, {
        showLoader: false,
        showMessage: false,
        showError: false
      })
      .pipe(map((lots: DsmRoomReplacementRulePendingLot[] | null) => lots ?? []));
  }

  /** Lots this rule has already run on. Same roles and error handling as `readPendingLots`. */
  readExecutedLots(ruleId: string): Observable<DsmRoomReplacementRuleExecutedLot[]> {
    return this.#api
      .getData<DsmRoomReplacementRuleExecutedLot[]>(`${this.#ruleUrl(ruleId)}/executed-lots`, {
        showLoader: false,
        showMessage: false,
        showError: false
      })
      .pipe(map((lots: DsmRoomReplacementRuleExecutedLot[] | null) => lots ?? []));
  }

  deleteRule(ruleId: string, showLoader: boolean = true): Observable<void> {
    return this.#api.deleteData<void>(this.#ruleUrl(ruleId), { showLoader: false, showMessage: false });
  }

  /** Every rule endpoint is keyed on the rule id alone — none are project-scoped. */
  #ruleUrl(ruleId: string): string {
    return `${this.#baseUrl}/room-replacement-rules/${ruleId}`;
  }
}
